import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, CalendarDays, CalendarClock, Users, Briefcase, Mail, LogOut, Plus, Pencil, Trash2, Clock, MapPin, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const statusColors = {
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-blue-100 text-blue-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

const emptyDoctor = { name: "", qualifications: "", specialization: "", experience_years: 0, languages: [], photo_url: "", bio_en: "", bio_ms: "", available: true };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [services, setServices] = useState([]);
  const [messages, setMessages] = useState([]);
  const [doctorForm, setDoctorForm] = useState(null);
  const [serviceForm, setServiceForm] = useState(null);
  const [scheduleDate, setScheduleDate] = useState(new Date().toISOString().split("T")[0]);

  const loadAll = useCallback(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/doctors").then((r) => setDoctors(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/contact").then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
    else if (user) loadAll();
  }, [user, navigate, loadAll]);

  if (!user) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading...</div>;

  const updateBooking = async (id, patch) => {
    try {
      await api.patch(`/bookings/${id}`, patch);
      loadAll();
      toast.success("Booking updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveDoctor = async () => {
    try {
      const payload = { ...doctorForm, experience_years: Number(doctorForm.experience_years) || 0, languages: typeof doctorForm.languages === "string" ? doctorForm.languages.split(",").map((s) => s.trim()).filter(Boolean) : doctorForm.languages };
      if (doctorForm.id) await api.put(`/doctors/${doctorForm.id}`, payload);
      else await api.post("/doctors", payload);
      setDoctorForm(null);
      loadAll();
      toast.success("Doctor saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteDoctor = async (id) => {
    try { await api.delete(`/doctors/${id}`); loadAll(); toast.success("Doctor deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveService = async () => {
    try {
      await api.put(`/services/${serviceForm.id}`, { name_en: serviceForm.name_en, name_ms: serviceForm.name_ms, desc_en: serviceForm.desc_en, desc_ms: serviceForm.desc_ms, price_from: Number(serviceForm.price_from), duration: serviceForm.duration, active: serviceForm.active });
      setServiceForm(null);
      loadAll();
      toast.success("Service updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const markRead = async (id) => {
    await api.patch(`/contact/${id}/read`).catch(() => {});
    loadAll();
  };

  const navItems = [
    { id: "bookings", icon: CalendarDays, label: "Bookings" },
    { id: "schedule", icon: CalendarClock, label: "Day Schedule" },
    { id: "doctors", icon: Users, label: "Doctors" },
    { id: "services", icon: Briefcase, label: "Services" },
    { id: "messages", icon: Mail, label: "Messages" },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-16 sm:w-60 bg-white border-r border-slate-200 flex flex-col shrink-0">
        <div className="flex items-center gap-2 px-4 h-16 border-b border-slate-100">
          <span className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
            <Stethoscope className="w-4 h-4 text-white" />
          </span>
          <span className="font-heading font-semibold hidden sm:block">Admin</span>
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {navItems.map(({ id, icon: Icon, label }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${tab === id ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-slate-50"}`}>
              <Icon className="w-5 h-5 shrink-0" /><span className="hidden sm:block">{label}</span>
              {id === "messages" && stats?.new_messages > 0 && <Badge className="ml-auto bg-red-500 hidden sm:flex">{stats.new_messages}</Badge>}
            </button>
          ))}
        </nav>
        <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn"
          className="flex items-center gap-3 px-5 py-4 text-sm text-slate-500 hover:text-red-600 border-t border-slate-100 transition-colors">
          <LogOut className="w-5 h-5 shrink-0" /><span className="hidden sm:block">Logout</span>
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 p-4 sm:p-8 overflow-x-auto">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              ["Total Bookings", stats.total_bookings, "total-bookings-stat"],
              ["Pending", stats.pending_bookings, "pending-bookings-stat"],
              ["Confirmed", stats.confirmed_bookings, "confirmed-bookings-stat"],
              ["New Messages", stats.new_messages, "new-messages-stat"],
            ].map(([label, val, tid]) => (
              <div key={label} className="bg-white rounded-2xl border border-slate-200 p-5">
                <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
                <p data-testid={tid} className="font-heading text-3xl font-semibold text-slate-900 mt-1">{val}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "bookings" && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <h2 className="font-heading text-lg font-semibold px-6 py-4 border-b border-slate-100">Bookings</h2>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ref</TableHead><TableHead>Patient</TableHead><TableHead>Service</TableHead>
                  <TableHead>Date/Time</TableHead><TableHead>Area</TableHead><TableHead>Doctor</TableHead><TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No bookings yet</TableCell></TableRow>}
                {bookings.map((b) => (
                  <TableRow key={b.id} data-testid={`booking-row-${b.booking_ref}`}>
                    <TableCell className="font-mono text-xs">{b.booking_ref}</TableCell>
                    <TableCell>
                      <p className="font-medium">{b.patient_name}</p>
                      <p className="text-xs text-slate-400">{b.phone}</p>
                    </TableCell>
                    <TableCell className="text-sm">{b.service_name}</TableCell>
                    <TableCell className="text-sm">{b.date}<br /><span className="text-xs text-slate-400">{b.time}</span></TableCell>
                    <TableCell className="text-sm">{b.area}</TableCell>
                    <TableCell>
                      <Select value={b.doctor_id || "none"} onValueChange={(v) => updateBooking(b.id, { doctor_id: v === "none" ? "" : v })}>
                        <SelectTrigger className="w-36 h-8 text-xs" data-testid={`booking-doctor-select-${b.booking_ref}`}><SelectValue placeholder="Assign" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Unassigned</SelectItem>
                          {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select value={b.status} onValueChange={(v) => updateBooking(b.id, { status: v })}>
                        <SelectTrigger className={`w-32 h-8 text-xs border-0 ${statusColors[b.status]}`} data-testid={`booking-status-select-${b.booking_ref}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["pending", "confirmed", "completed", "cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {tab === "schedule" && (() => {
          const dayBookings = bookings.filter((b) => b.date === scheduleDate && b.status === "confirmed");
          const groups = [...doctors.map((d) => ({ key: d.id, doctor: d, visits: dayBookings.filter((b) => b.doctor_id === d.id) })),
            { key: "unassigned", doctor: null, visits: dayBookings.filter((b) => !b.doctor_id || !doctors.some((d) => d.id === b.doctor_id)) }]
            .filter((g) => g.visits.length > 0)
            .map((g) => ({ ...g, visits: [...g.visits].sort((a, b) => a.time.localeCompare(b.time)) }));
          return (
            <div data-testid="schedule-view">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h2 className="font-heading text-lg font-semibold">Confirmed Visits — {scheduleDate}</h2>
                <Input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)}
                  data-testid="schedule-date-input" className="w-44 rounded-full" />
              </div>
              {groups.length === 0 && (
                <p data-testid="schedule-empty" className="text-slate-400 bg-white rounded-2xl border border-slate-200 p-10 text-center">
                  No confirmed visits on this date
                </p>
              )}
              <div className="space-y-6">
                {groups.map(({ key, doctor, visits }) => (
                  <div key={key} data-testid={`schedule-group-${key}`} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                      {doctor ? (
                        <>
                          <img src={doctor.photo_url || undefined} alt={doctor.name} className="w-9 h-9 rounded-full object-cover" />
                          <div>
                            <p className="font-semibold text-slate-900">{doctor.name}</p>
                            <p className="text-xs text-slate-400">{doctor.specialization}</p>
                          </div>
                        </>
                      ) : (
                        <p className="font-semibold text-amber-600">Unassigned</p>
                      )}
                      <Badge className="ml-auto bg-blue-100 text-blue-700">{visits.length} visit{visits.length > 1 ? "s" : ""}</Badge>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {visits.map((v) => (
                        <div key={v.id} data-testid={`schedule-visit-${v.booking_ref}`} className="flex flex-wrap items-center gap-x-6 gap-y-2 px-6 py-4 text-sm">
                          <span className="flex items-center gap-2 font-heading font-semibold text-primary w-20"><Clock className="w-4 h-4" /> {v.time}</span>
                          <span className="font-medium text-slate-900 min-w-36">{v.patient_name}</span>
                          <span className="text-slate-500">{v.service_name}</span>
                          <span className="flex items-center gap-1.5 text-slate-500"><MapPin className="w-3.5 h-3.5 text-slate-400" /> {v.area}</span>
                          <a href={`tel:${v.phone}`} className="flex items-center gap-1.5 text-slate-500 hover:text-primary transition-colors"><Phone className="w-3.5 h-3.5 text-slate-400" /> {v.phone}</a>
                          <span className="text-xs font-mono text-slate-400 ml-auto">{v.booking_ref}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {tab === "doctors" && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="font-heading text-lg font-semibold">Doctors</h2>
              <Button size="sm" data-testid="add-doctor-btn" className="rounded-full" onClick={() => setDoctorForm({ ...emptyDoctor })}>
                <Plus className="w-4 h-4 mr-1" /> Add Doctor
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow><TableHead>Doctor</TableHead><TableHead>Specialization</TableHead><TableHead>Experience</TableHead><TableHead>Available</TableHead><TableHead></TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {doctors.map((d) => (
                  <TableRow key={d.id} data-testid={`admin-doctor-row-${d.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <img src={d.photo_url || undefined} alt={d.name} className="w-9 h-9 rounded-full object-cover" />
                        <div><p className="font-medium">{d.name}</p><p className="text-xs text-slate-400">{d.qualifications}</p></div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{d.specialization}</TableCell>
                    <TableCell className="text-sm">{d.experience_years} yrs</TableCell>
                    <TableCell><Badge className={d.available ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>{d.available ? "Yes" : "No"}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" data-testid={`edit-doctor-${d.id}`} onClick={() => setDoctorForm({ ...d, languages: (d.languages || []).join(", ") })}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" data-testid={`delete-doctor-${d.id}`} className="text-red-500" onClick={() => deleteDoctor(d.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {tab === "services" && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <h2 className="font-heading text-lg font-semibold px-6 py-4 border-b border-slate-100">Services</h2>
            <Table>
              <TableHeader>
                <TableRow><TableHead>Service</TableHead><TableHead>Price From</TableHead><TableHead>Duration</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {services.map((s) => (
                  <TableRow key={s.id} data-testid={`admin-service-row-${s.id}`}>
                    <TableCell><p className="font-medium">{s.name_en}</p><p className="text-xs text-slate-400">{s.name_ms}</p></TableCell>
                    <TableCell>RM {s.price_from}</TableCell>
                    <TableCell className="text-sm">{s.duration}</TableCell>
                    <TableCell><Badge className={s.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>{s.active ? "Active" : "Off"}</Badge></TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" data-testid={`edit-service-${s.id}`} onClick={() => setServiceForm({ ...s })}><Pencil className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-4">
            <h2 className="font-heading text-lg font-semibold">Contact Messages</h2>
            {messages.length === 0 && <p className="text-slate-400 bg-white rounded-2xl border border-slate-200 p-10 text-center">No messages yet</p>}
            {messages.map((m) => (
              <div key={m.id} data-testid={`message-card-${m.id}`} className={`bg-white rounded-2xl border p-6 ${m.status === "new" ? "border-primary/40" : "border-slate-200"}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <p className="font-semibold">{m.name}</p>
                    {m.status === "new" && <Badge className="bg-blue-100 text-blue-700">New</Badge>}
                  </div>
                  {m.status === "new" && <Button variant="outline" size="sm" className="rounded-full" data-testid={`mark-read-${m.id}`} onClick={() => markRead(m.id)}>Mark read</Button>}
                </div>
                <p className="text-xs text-slate-400 mb-3">{m.email} {m.phone && `• ${m.phone}`} • {new Date(m.created_at).toLocaleString()}</p>
                <p className="text-sm text-slate-600">{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Doctor Dialog */}
      <Dialog open={!!doctorForm} onOpenChange={(o) => !o && setDoctorForm(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{doctorForm?.id ? "Edit Doctor" : "Add Doctor"}</DialogTitle></DialogHeader>
          {doctorForm && (
            <div className="space-y-4">
              <div><Label>Name</Label><Input data-testid="doctor-form-name" value={doctorForm.name} onChange={(e) => setDoctorForm({ ...doctorForm, name: e.target.value })} className="mt-1" /></div>
              <div><Label>Qualifications</Label><Input data-testid="doctor-form-quals" value={doctorForm.qualifications} onChange={(e) => setDoctorForm({ ...doctorForm, qualifications: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Specialization</Label><Input data-testid="doctor-form-spec" value={doctorForm.specialization} onChange={(e) => setDoctorForm({ ...doctorForm, specialization: e.target.value })} className="mt-1" /></div>
                <div><Label>Experience (years)</Label><Input type="number" data-testid="doctor-form-exp" value={doctorForm.experience_years} onChange={(e) => setDoctorForm({ ...doctorForm, experience_years: e.target.value })} className="mt-1" /></div>
              </div>
              <div><Label>Languages (comma separated)</Label><Input data-testid="doctor-form-langs" value={doctorForm.languages} onChange={(e) => setDoctorForm({ ...doctorForm, languages: e.target.value })} className="mt-1" /></div>
              <div><Label>Photo URL</Label><Input data-testid="doctor-form-photo" value={doctorForm.photo_url} onChange={(e) => setDoctorForm({ ...doctorForm, photo_url: e.target.value })} className="mt-1" /></div>
              <div><Label>Bio (English)</Label><Textarea rows={2} data-testid="doctor-form-bio-en" value={doctorForm.bio_en} onChange={(e) => setDoctorForm({ ...doctorForm, bio_en: e.target.value })} className="mt-1" /></div>
              <div><Label>Bio (Bahasa Malaysia)</Label><Textarea rows={2} data-testid="doctor-form-bio-ms" value={doctorForm.bio_ms} onChange={(e) => setDoctorForm({ ...doctorForm, bio_ms: e.target.value })} className="mt-1" /></div>
              <div className="flex items-center gap-3"><Switch checked={doctorForm.available} onCheckedChange={(v) => setDoctorForm({ ...doctorForm, available: v })} data-testid="doctor-form-available" /><Label>Available for bookings</Label></div>
              <Button onClick={saveDoctor} data-testid="doctor-form-save" className="w-full rounded-full">Save Doctor</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Service Dialog */}
      <Dialog open={!!serviceForm} onOpenChange={(o) => !o && setServiceForm(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Service</DialogTitle></DialogHeader>
          {serviceForm && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Name (EN)</Label><Input data-testid="service-form-name-en" value={serviceForm.name_en} onChange={(e) => setServiceForm({ ...serviceForm, name_en: e.target.value })} className="mt-1" /></div>
                <div><Label>Name (BM)</Label><Input data-testid="service-form-name-ms" value={serviceForm.name_ms} onChange={(e) => setServiceForm({ ...serviceForm, name_ms: e.target.value })} className="mt-1" /></div>
              </div>
              <div><Label>Description (EN)</Label><Textarea rows={2} data-testid="service-form-desc-en" value={serviceForm.desc_en} onChange={(e) => setServiceForm({ ...serviceForm, desc_en: e.target.value })} className="mt-1" /></div>
              <div><Label>Description (BM)</Label><Textarea rows={2} data-testid="service-form-desc-ms" value={serviceForm.desc_ms} onChange={(e) => setServiceForm({ ...serviceForm, desc_ms: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Price From (RM)</Label><Input type="number" data-testid="service-form-price" value={serviceForm.price_from} onChange={(e) => setServiceForm({ ...serviceForm, price_from: e.target.value })} className="mt-1" /></div>
                <div><Label>Duration</Label><Input data-testid="service-form-duration" value={serviceForm.duration} onChange={(e) => setServiceForm({ ...serviceForm, duration: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="flex items-center gap-3"><Switch checked={serviceForm.active} onCheckedChange={(v) => setServiceForm({ ...serviceForm, active: v })} data-testid="service-form-active" /><Label>Active</Label></div>
              <Button onClick={saveService} data-testid="service-form-save" className="w-full rounded-full">Save Service</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
